import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";
import { Wordmark } from "@/components/Brand";
import ThemeToggle from "@/components/ThemeToggle";
import { useAuth } from "@/contexts/AuthContext";

const Login = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#050B14] text-white flex flex-col" data-testid="login-page">
      <SEO title="Sign in" description="Sign in to ClydeFlow with Google." path="/login" noindex />
      <div className="px-6 lg:px-10 pt-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm" data-testid="login-back-link">
          <ArrowLeft size={16} /> Back to site
        </Link>
        <ThemeToggle />
      </div>

      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <Wordmark className="h-16 mx-auto mb-8" />
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-400 mb-3">
              Sign in
            </p>
            <h1 className="font-heading text-3xl font-black tracking-tighter text-white">
              Welcome to the Flow.
            </h1>
            <p className="mt-3 text-slate-400 text-sm">
              One tap. Secured by Google.
            </p>
          </div>

          <button
            onClick={handleLogin}
            data-testid="login-google-btn"
            className="w-full py-4 bg-white text-[#050B14] font-bold text-sm rounded-sm hover:bg-slate-100 transition-all flex items-center justify-center gap-3"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.83z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            Continue with Google
          </button>

          <p className="mt-8 text-center text-xs text-slate-500 font-mono uppercase tracking-[0.2em]">
            By signing in you agree to our
            <Link to="/terms" className="text-cyan-300 hover:text-white ml-1">Terms</Link>
            <span className="mx-1">·</span>
            <Link to="/privacy" className="text-cyan-300 hover:text-white">Privacy</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
